<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>

        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.packageNumber')"
                v-model.trim="filterOptions.package_number"
              />

              <base-select-input
                col="4"
                :optionsList="returnStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.status"
              />

              <base-input
                col="4"
                type="text"
                :placeholder="$t('RETURN_REQUESTS.driverName')"
                v-model.trim="filterOptions.driver_name"
              />

              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from')"
                v-model="filterOptions.from"
              />
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to')"
                v-model="filterOptions.to"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.returnRequests") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.id`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>

        <template v-slot:[`item.package_number`]="{ item }">
          <span class="text-primary font-weight-bold">{{
            item.package_number || "-"
          }}</span>
        </template>

        <template v-slot:[`item.bundle_number`]="{ item }">
          {{ item.bundle_number || "-" }}
        </template>

        <template v-slot:[`item.user.name`]="{ item }">
          {{ item.user?.name || "-" }}
        </template>

        <template v-slot:[`item.mobile`]="{ item }">
          <span dir="ltr">{{ item.mobile || "-" }}</span>
        </template>

        <template v-slot:[`item.address`]="{ item }">
          <span
            class="text-truncate d-inline-block"
            style="max-width: 220px"
            :title="item.address"
          >
            {{ item.address || "-" }}
          </span>
        </template>

        <template v-slot:[`item.status.text`]="{ item }">
          <span
            class="status_badge"
            :class="getStatusClass(item.status?.value)"
          >
            {{ getStatusText(item.status?.value) }}
          </span>
        </template>

        <template v-slot:[`item.driver.name`]="{ item }">
          {{ item.driver?.name || "-" }}
        </template>

        <template v-slot:[`item.driver.mobile`]="{ item }">
          <span dir="ltr">{{ item.driver?.mobile || "-" }}</span>
        </template>

        <template v-slot:[`item.last_status_update`]="{ item }">
          {{ formatLastUpdateTime(item.status_history) }}
        </template>

        <template v-slot:[`item.notes`]="{ item }">
          <span
            v-if="item.notes"
            class="text-truncate d-inline-block"
            style="max-width: 180px; cursor: pointer"
            :title="item.notes"
            @click="showNotesModal(item.notes)"
          >
            {{ item.notes }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="table_actions">
            <a-tooltip placement="top">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button
                type="button"
                class="table_action_btn table_action_btn--view"
                :aria-label="$t('BUTTONS.show')"
                @click="showItem(item)"
              >
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="top"
              v-if="isReturningStatus(item.status?.value)"
            >
              <template slot="title">
                <span>{{ $t("RETURN_REQUESTS.scanQr") }}</span>
              </template>
              <button
                type="button"
                class="table_action_btn table_action_btn--scan"
                :aria-label="$t('RETURN_REQUESTS.scanQr')"
                @click="openScanDialog(item)"
              >
                <i class="fal fa-qrcode"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="top"
              v-if="isReturnedStatus(item.status?.value)"
            >
              <template slot="title">
                <span>{{ $t("RETURN_REQUESTS.moveToPending") }}</span>
              </template>
              <button
                type="button"
                class="table_action_btn table_action_btn--pending"
                :aria-label="$t('RETURN_REQUESTS.moveToPending')"
                @click="setPackageToPending(item)"
              >
                <i class="fal fa-redo"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:top>
          <description-modal
            v-if="dialogNotes"
            :modalIsOpen="dialogNotes"
            :modalDesc="selectedNotes"
            @toggleModal="dialogNotes = !dialogNotes"
          />

          <redelivery-confirmation-modal
            v-if="dialogRedeliveryConfirm"
            :modalAppearanceData="dialogRedeliveryConfirm"
            :selectedItem="selectedItemForRedelivery"
            :isSubmitting="isRedeliverySubmitting"
            :errorMessage="redeliveryErrorMessage"
            @controleModalApperance="
              dialogRedeliveryConfirm = false;
              redeliveryErrorMessage = '';
            "
            @confirmRedelivery="handleRedeliveryConfirm"
          />

          <v-dialog
            v-model="dialogScan"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            max-width="520"
            content-class="return-scan-dialog"
            persistent
            scrollable
          >
            <v-card>
              <v-card-title class="text-h6 justify-center">{{
                $t("RETURN_REQUESTS.scanQr")
              }}</v-card-title>
              <v-card-text>
                <p class="mb-3">
                  {{ $t("RETURN_REQUESTS.packageNumberLabel") }}:
                  <strong>{{
                    selectedItemForScan?.package_number || "-"
                  }}</strong>
                </p>

                <div class="mb-3">
                  <div class="scanner_header mb-2">
                    <p class="mb-0">
                      {{ $t("RETURN_REQUESTS.scanningWithCamera") }}
                    </p>
                    <span
                      class="scanner_state_badge"
                      :class="{ active: isScannerActive }"
                    >
                      {{
                        isScannerActive
                          ? $t("RETURN_REQUESTS.cameraOn")
                          : $t("RETURN_REQUESTS.cameraOff")
                      }}
                    </span>
                  </div>

                  <p class="scanner_hint mb-2">
                    {{ $t("RETURN_REQUESTS.scanHint") }}
                  </p>

                  <div v-if="availableCameras.length > 1" class="mb-2">
                    <label class="camera_select_label mb-1 d-block">
                      {{ $t("RETURN_REQUESTS.chooseCamera") }}
                    </label>
                    <select
                      class="camera_select"
                      v-model="selectedCameraId"
                      @change="onCameraChange"
                    >
                      <option
                        v-for="camera in availableCameras"
                        :key="camera.id"
                        :value="camera.id"
                      >
                        {{
                          camera.label ||
                          `${$t("RETURN_REQUESTS.camera")} ${camera.id}`
                        }}
                      </option>
                    </select>
                  </div>

                  <div class="scanner_actions mb-2">
                    <v-btn
                      small
                      outlined
                      color="primary"
                      :loading="isScannerStarting"
                      :disabled="isScannerStarting"
                      @click="startQrScanner(selectedCameraId)"
                    >
                      {{ $t("RETURN_REQUESTS.restartCamera") }}
                    </v-btn>

                    <v-btn
                      small
                      text
                      color="error"
                      :disabled="!isScannerActive && !isScannerStarting"
                      @click="stopQrScanner"
                    >
                      {{ $t("RETURN_REQUESTS.stopCamera") }}
                    </v-btn>
                  </div>

                  <div
                    class="scanner_surface"
                    :class="{ loading: isScannerStarting }"
                  >
                    <div id="return-request-qr-reader"></div>
                  </div>

                  <small
                    v-if="cameraScanError"
                    class="text-danger d-block mt-2"
                  >
                    {{ cameraScanError }}
                  </small>
                </div>

                <div class="row">
                  <base-input
                    col="12"
                    type="text"
                    :placeholder="$t('RETURN_REQUESTS.scanOrEnterQr')"
                    v-model.trim="scannedToken"
                  />
                </div>
              </v-card-text>
              <v-card-actions class="scan_actions">
                <v-btn
                  class="modal_confirm_btn"
                  :loading="isSubmittingScan"
                  :disabled="isSubmittingScan"
                  @click="confirmReturnByQr"
                >
                  {{ $t("RETURN_REQUESTS.confirmReturn") }}
                </v-btn>
                <v-btn class="modal_cancel_btn" @click="closeScanDialog">
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </main>

    <div class="pagination_container text-center mt-3 mb-0">
      <v-pagination
        class="py-0"
        square
        v-model="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="6"
        @input="updateRouterQueryParam($event)"
        :prev-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
        "
        :next-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
        "
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Html5Qrcode } from "html5-qrcode";
import RedeliveryConfirmationModal from "../../../components/modals/RedeliveryConfirmationModal.vue";

export default {
  name: "PackageReturnRequests",

  components: {
    RedeliveryConfirmationModal,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    returnStatuses() {
      return [
        {
          id: "under_return",
          name: this.$t("RETURN_REQUESTS.statuses.under_return"),
          value: "under_return",
        },
        // {
        //   id: "return_accepted",
        //   name: this.$t("RETURN_REQUESTS.statuses.return_accepted"),
        //   value: "return_accepted",
        // },
        {
          id: "returning",
          name: this.$t("RETURN_REQUESTS.statuses.returning"),
          value: "returning",
        },
        {
          id: "returned",
          name: this.$t("RETURN_REQUESTS.statuses.returned"),
          value: "returned",
        },
      ];
    },

    dateFilterTypes() {
      return [
        {
          id: "created_at",
          name: this.$t("RETURN_REQUESTS.dateTypes.created_at"),
          value: "created_at",
        },
        {
          id: "delivered_at",
          name: this.$t("RETURN_REQUESTS.dateTypes.delivered_at"),
          value: "delivered_at",
        },
      ];
    },
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,
      isSubmittingScan: false,
      isSubmittingPending: false,

      filterFormIsActive: false,
      filterOptions: {
        package_number: null,
        status: null,
        driver_name: null,
        created_at: null,
      },

      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.packageNumber"),
          value: "package_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.bundleNumber"),
          value: "bundle_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.recipientName"),
          value: "user.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.recipientPhone"),
          value: "mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.address"),
          value: "address",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status.text",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.deliveryRepName"),
          value: "driver.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.deliveryRepPhone"),
          value: "driver.mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.lastUpdate"),
          value: "last_status_update",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.notes"),
          value: "notes",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "150px",
        },
      ],
      tableRows: [],

      dialogNotes: false,
      selectedNotes: "",

      dialogRedeliveryConfirm: false,
      selectedItemForRedelivery: null,
      isRedeliverySubmitting: false,
      redeliveryErrorMessage: "",

      dialogScan: false,
      selectedItemForScan: null,
      scannedToken: null,
      qrScanner: null,
      cameraScanError: "",
      availableCameras: [],
      selectedCameraId: null,
      isScannerStarting: false,
      isScannerActive: false,

      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/packages/return-requests",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.package_number = null;
      this.filterOptions.status = null;
      this.filterOptions.driver_name = null;
      this.filterOptions.created_at = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/packages/return-requests",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      try {
        const params = {
          page: this.paginations.current_page,
          package_number: this.filterOptions.package_number,
          status: this.filterOptions.status?.value,
          search: this.filterOptions.driver_name,
          from: this.filterOptions.from,
          to: this.filterOptions.to,
        };

        const res = await this.$axios({
          method: "GET",
          url: "packages/return-requests",
          params,
        });

        this.tableRows = res.data.data || [];
        this.paginations.last_page = res.data.meta?.last_page || 1;
        this.paginations.items_per_page = res.data.meta?.per_page || 10;
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("RETURN_REQUESTS.loadError")
        );
      } finally {
        this.loading = false;
      }
    },

    showItem(item) {
      this.$router.push({ path: `/packages/show/${item.id}` });
    },

    openScanDialog(item) {
      this.selectedItemForScan = item;
      this.scannedToken = null;
      this.cameraScanError = "";
      this.availableCameras = [];
      this.selectedCameraId = null;
      this.dialogScan = true;
      this.$nextTick(() => {
        this.initializeQrScanner();
      });
    },

    async closeScanDialog() {
      await this.stopQrScanner();
      this.dialogScan = false;
      this.selectedItemForScan = null;
      this.scannedToken = null;
      this.cameraScanError = "";
      this.availableCameras = [];
      this.selectedCameraId = null;
    },

    async initializeQrScanner() {
      await this.stopQrScanner();
      this.cameraScanError = "";
      this.isScannerStarting = true;

      try {
        const cameras = await Html5Qrcode.getCameras();

        if (!cameras || cameras.length === 0) {
          this.cameraScanError = this.$t("RETURN_REQUESTS.cameraNotFound");
          this.isScannerActive = false;
          return;
        }

        this.availableCameras = cameras;

        const preferredCamera =
          cameras.find((camera) =>
            /back|rear|environment/i.test(camera.label || "")
          ) || cameras[0];

        this.selectedCameraId = preferredCamera.id;

        await this.startQrScanner(this.selectedCameraId);
      } catch (error) {
        this.cameraScanError = this.$t("RETURN_REQUESTS.cameraAccessError");
        this.isScannerActive = false;
      } finally {
        this.isScannerStarting = false;
      }
    },

    async startQrScanner(cameraId) {
      if (!this.dialogScan) {
        return;
      }

      const targetCameraId =
        cameraId || this.selectedCameraId || this.availableCameras[0]?.id;

      if (!targetCameraId) {
        this.cameraScanError = this.$t("RETURN_REQUESTS.cameraNotFound");
        return;
      }

      this.isScannerStarting = true;
      this.cameraScanError = "";

      await this.stopQrScanner();

      try {
        this.selectedCameraId = targetCameraId;

        this.qrScanner = new Html5Qrcode("return-request-qr-reader");

        await this.qrScanner.start(
          { deviceId: { exact: targetCameraId } },
          {
            fps: this.$vuetify.breakpoint.xsOnly ? 8 : 10,
            qrbox: this.$vuetify.breakpoint.xsOnly
              ? { width: 180, height: 180 }
              : { width: 220, height: 220 },
            aspectRatio: 1,
          },
          (decodedText) => {
            this.handleDecodedQr(decodedText);
          }
        );

        this.isScannerActive = true;
      } catch (error) {
        this.cameraScanError = this.$t("RETURN_REQUESTS.cameraAccessError");
        this.isScannerActive = false;
      } finally {
        this.isScannerStarting = false;
      }
    },

    async stopQrScanner() {
      const scanner = this.qrScanner;

      if (!scanner) {
        this.isScannerActive = false;
        return;
      }

      this.qrScanner = null;

      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (error) {}

      try {
        await scanner.clear();
      } catch (error) {}

      this.isScannerActive = false;
    },

    onCameraChange() {
      this.startQrScanner(this.selectedCameraId);
    },

    async handleDecodedQr(decodedText) {
      const extractedToken = this.extractTokenFromQr(decodedText);

      if (!extractedToken) {
        return;
      }

      this.scannedToken = extractedToken;
      await this.stopQrScanner();
    },

    extractTokenFromQr(decodedText) {
      if (!decodedText || typeof decodedText !== "string") {
        return null;
      }

      const normalizedValue = decodedText.trim();
      if (!normalizedValue) {
        return null;
      }

      const tokenKeys = ["token", "tracking_token", "qr", "code"];

      const parsedJson = this.tryParseJson(normalizedValue);
      if (parsedJson) {
        for (let index = 0; index < tokenKeys.length; index++) {
          const key = tokenKeys[index];
          if (parsedJson[key]) {
            return String(parsedJson[key]).trim();
          }
        }
      }

      const parsedUrl = this.tryParseUrl(normalizedValue);
      if (parsedUrl) {
        for (let index = 0; index < tokenKeys.length; index++) {
          const key = tokenKeys[index];
          const queryToken = parsedUrl.searchParams.get(key);
          if (queryToken) {
            return queryToken.trim();
          }
        }

        const pathSegments = parsedUrl.pathname
          .split("/")
          .filter((segment) => segment);
        if (pathSegments.length > 0) {
          return pathSegments[pathSegments.length - 1];
        }
      }

      return normalizedValue;
    },

    tryParseJson(rawValue) {
      try {
        return JSON.parse(rawValue);
      } catch (error) {
        return null;
      }
    },

    tryParseUrl(rawValue) {
      try {
        return new URL(rawValue);
      } catch (error) {
        return null;
      }
    },

    async confirmReturnByQr() {
      if (!this.scannedToken) {
        this.$message.error(this.$t("RETURN_REQUESTS.enterQr"));
        return;
      }

      if (
        this.selectedItemForScan?.tracking_token &&
        this.scannedToken !== this.selectedItemForScan.tracking_token
      ) {
        this.$message.error(this.$t("RETURN_REQUESTS.qrNotLinked"));
        return;
      }

      this.isSubmittingScan = true;
      try {
        await this.$axios({
          method: "POST",
          url: "packages/confirm-return",
          data: {
            token: this.scannedToken,
          },
        });

        this.$message.success(this.$t("RETURN_REQUESTS.returnConfirmed"));
        this.closeScanDialog();
        this.setTableRows();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("RETURN_REQUESTS.invalidQr")
        );
      } finally {
        this.isSubmittingScan = false;
      }
    },

    async setPackageToPending(item) {
      this.selectedItemForRedelivery = item;
      this.redeliveryErrorMessage = "";
      this.dialogRedeliveryConfirm = true;
    },

    async handleRedeliveryConfirm(item) {
      this.isRedeliverySubmitting = true;
      this.redeliveryErrorMessage = "";

      try {
        await this.trySetPendingRequest(item.package_number);
        this.$message.success(this.$t("RETURN_REQUESTS.movedToPending"));
        this.tableRows = this.tableRows.filter((row) => row.id !== item.id);
        // Close modal only on success
        this.dialogRedeliveryConfirm = false;
      } catch (error) {
        this.redeliveryErrorMessage =
          error.response?.data?.message ||
          this.$t("RETURN_REQUESTS.pendingFallbackError");
      } finally {
        this.isRedeliverySubmitting = false;
      }
    },

    async trySetPendingRequest(packageId) {
      const requests = [
        {
          method: "POST",
          url: `packages/${packageId}/re-deliver`,
          data: { status: "pending" },
        },
      ];

      let lastError = null;

      for (let index = 0; index < requests.length; index++) {
        try {
          await this.$axios(requests[index]);
          return;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError;
    },

    showNotesModal(notes) {
      this.dialogNotes = true;
      this.selectedNotes = notes;
    },

    isReturningStatus(statusValue) {
      return ["returning"].includes(statusValue);
    },

    isReturnedStatus(statusValue) {
      return statusValue === "returned";
    },

    getStatusText(statusValue) {
      const statusMap = {
        under_return: this.$t("RETURN_REQUESTS.statuses.under_return"),
        return_accepted: this.$t("RETURN_REQUESTS.statuses.return_accepted"),
        returning: this.$t("RETURN_REQUESTS.statuses.returning"),
        returned: this.$t("RETURN_REQUESTS.statuses.returned"),
      };

      return statusMap[statusValue] || statusValue || "-";
    },

    getStatusClass(statusValue) {
      const statusClassMap = {
        under_return: "status-under-return",
        return_accepted: "status-return-accepted",
        returning: "status-returning",
        returned: "status-returned",
      };

      return statusClassMap[statusValue] || "";
    },

    formatLastUpdateTime(statusHistory) {
      if (!statusHistory || statusHistory.length === 0) {
        return "-";
      }

      const lastHistory = statusHistory[statusHistory.length - 1];
      return this.formatDate(lastHistory.created_at);
    },

    formatDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString.replace(" ", "T"));
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
  },

  beforeDestroy() {
    this.stopQrScanner();
  },
};
</script>

<style lang="scss" scoped>
.status_badge {
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  display: inline-block;
  white-space: nowrap;
}

.status-under-return {
  background-color: #f39c12;
  color: white;
}

.status-return-accepted {
  background-color: #9b59b6;
  color: white;
}

.status-returning {
  background-color: #e67e22;
  color: white;
}

.status-returned {
  background-color: #2ecc71;
  color: white;
}

.table_actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 0;
}

.table_action_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;

  i {
    font-size: 15px;
    transition: color 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &--view {
    background: var(--soft_main_theme_clr);
    border-color: rgba(239, 52, 5, 0.15);

    i {
      color: var(--main_theme_clr);
    }

    &:hover {
      background: var(--main_theme_clr);
      border-color: var(--main_theme_clr);

      i {
        color: var(--white_clr);
      }
    }
  }

  &--scan {
    background: #e8f4fd;
    border-color: rgba(23, 162, 184, 0.2);

    i {
      color: #17a2b8;
    }

    &:hover {
      background: #17a2b8;
      border-color: #17a2b8;

      i {
        color: #fff;
      }
    }
  }

  &--pending {
    background: #fff4e5;
    border-color: rgba(243, 156, 18, 0.25);

    i {
      color: #e67e22;
    }

    &:hover {
      background: #e67e22;
      border-color: #e67e22;

      i {
        color: #fff;
      }
    }
  }
}

#return-request-qr-reader {
  width: 100%;
}

::v-deep .return-scan-dialog {
  width: min(520px, 95vw);
}

::v-deep .return-scan-dialog .v-card {
  max-height: 90vh;
  overflow-y: auto;
}

::v-deep .return-scan-dialog #return-request-qr-reader video {
  width: 100% !important;
  height: auto !important;
  border-radius: 8px;
}

.scanner_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.scanner_state_badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f3f5;
  color: #6c757d;
}

.scanner_state_badge.active {
  background: #e8f7ef;
  color: #1e7e34;
}

.scanner_hint {
  font-size: 12px;
  color: #6c757d;
}

.camera_select_label {
  font-size: 12px;
  font-weight: 600;
}

.camera_select {
  width: 100%;
  min-height: 36px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
}

.scanner_actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.scanner_surface {
  position: relative;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px;
  min-height: 220px;
  background: #fafafa;
}

.scanner_surface.loading {
  opacity: 0.75;
}

.scan_actions {
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .scan_actions {
    justify-content: stretch;
  }

  .scan_actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }

  .scanner_actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }

  .scanner_surface {
    min-height: 180px;
    padding: 8px;
  }

  ::v-deep .return-scan-dialog .v-card {
    max-height: 100vh;
    border-radius: 0;
  }
}
</style>
